import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Plan {
  name: string;
  price: string;
  period: string;
  features: string[];
  popular?: boolean;
}

const plans: Plan[] = [
  {
    name: 'Starter',
    price: '$19',
    period: '/month',
    features: [
      '1 App',
      'Basic Templates',
      'Community Support',
      'iOS & Android Export',
      '5GB Storage'
    ]
  },
  {
    name: 'Pro',
    price: '$49',
    period: '/month',
    popular: true,
    features: [
      '5 Apps',
      'All Templates',
      'Priority Support',
      'Custom Domain',
      '50GB Storage',
      'Advanced Analytics'
    ]
  },
  {
    name: 'Enterprise',
    price: '$99',
    period: '/month',
    features: [
      'Unlimited Apps',
      'White-Label',
      'API Access',
      'Dedicated Support',
      'Unlimited Storage',
      'Custom Integrations'
    ]
  }
];

const templates = [
  {
    id: 1,
    name: 'Fashion Store',
    category: 'eCommerce',
    image: 'https://public.readdy.ai/ai/img_res/576d11886237e693c7f992b23d72dde0.jpg'
  },
  {
    id: 2,
    name: 'Restaurant Menu',
    category: 'Food & Drink',
    image: 'https://readdy.ai/api/search-image?query=elegant%20restaurant%20food%20ordering%20mobile%20app%20interface%20with%20clean%20white%20background%20menu%20items%20food%20photos%20modern%20typography%20professional%20ui%20design&width=300&height=450&seq=onboard2&orientation=portrait'
  },
  {
    id: 3,
    name: 'Fitness Tracker',
    category: 'Health & Fitness',
    image: 'https://readdy.ai/api/search-image?query=modern%20fitness%20workout%20tracking%20mobile%20app%20interface%20with%20clean%20white%20background%20exercise%20cards%20progress%20charts%20vibrant%20colors%20professional%20ui%20design&width=300&height=450&seq=onboard3&orientation=portrait'
  },
  {
    id: 4,
    name: 'Property Listings',
    category: 'Real Estate',
    image: 'https://readdy.ai/api/search-image?query=professional%20real%20estate%20property%20listing%20mobile%20app%20interface%20with%20clean%20white%20background%20property%20cards%20map%20view%20modern%20typography%20elegant%20ui%20design&width=300&height=450&seq=onboard4&orientation=portrait'
  },
  {
    id: 5,
    name: 'Social Network',
    category: 'Social',
    image: 'https://readdy.ai/api/search-image?query=modern%20social%20media%20networking%20mobile%20app%20interface%20with%20clean%20white%20background%20feed%20posts%20user%20profiles%20messaging%20clean%20typography%20professional%20ui%20design&width=300&height=450&seq=onboard5&orientation=portrait'
  },
  {
    id: 6,
    name: 'Appointment Booking',
    category: 'Booking',
    image: 'https://readdy.ai/api/search-image?query=clean%20appointment%20booking%20scheduling%20mobile%20app%20interface%20with%20white%20background%20calendar%20view%20time%20slots%20modern%20typography%20professional%20ui%20design&width=300&height=450&seq=onboard6&orientation=portrait'
  }
];

export default function GetStartedPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleLaunch = () => {
    navigate('/builder', {
      state: {
        selectedPlan,
        selectedTemplate,
        formData
      }
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Progress Indicator */}
          <div className="mb-16">
            <div className="flex items-center justify-center gap-4 mb-4">
              {[1, 2, 3].map((num) => (
                <div key={num} className="flex items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                    step >= num 
                      ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {num}
                  </div>
                  {num < 3 && (
                    <div className={`w-24 h-1 mx-2 transition-all ${
                      step > num ? 'bg-teal-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-32 text-sm font-medium text-gray-600">
              <span className={step === 1 ? 'text-teal-500' : ''}>Choose Plan</span>
              <span className={step === 2 ? 'text-teal-500' : ''}>Create Account</span>
              <span className={step === 3 ? 'text-teal-500' : ''}>Pick Template</span>
            </div>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {/* Step 1: Choose Plan */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-12">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Choose Your Plan
                  </h2>
                  <p className="text-xl text-gray-600">
                    Select the perfect plan for your app building needs
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  {plans.map((plan) => (
                    <div
                      key={plan.name}
                      onClick={() => setSelectedPlan(plan.name)}
                      className={`relative p-8 rounded-2xl border-2 transition-all cursor-pointer ${
                        selectedPlan === plan.name
                          ? 'border-teal-500 shadow-2xl shadow-teal-500/20 scale-105'
                          : 'border-gray-200 hover:border-teal-300 hover:shadow-xl'
                      }`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <span className="px-4 py-1 bg-teal-500 text-white text-sm font-semibold rounded-full shadow-lg">
                            Most Popular
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {plan.name}
                        </h3>
                        <div className="flex items-baseline justify-center">
                          <span className="text-5xl font-bold">{plan.price}</span>
                          <span className="text-gray-600 ml-2">{plan.period}</span>
                        </div>
                      </div>

                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-check-line text-teal-500 text-xl mr-3"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {selectedPlan === plan.name && (
                        <div className="absolute top-4 right-4">
                          <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-lg"></i>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handleNext}
                    disabled={!selectedPlan}
                    className={`px-12 py-4 rounded-lg font-semibold text-lg transition-all whitespace-nowrap ${
                      selectedPlan
                        ? 'bg-teal-500 text-white hover:bg-teal-600 shadow-lg shadow-teal-500/30 cursor-pointer'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Continue to Account Setup
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Create Account */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-12">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Create Your Account
                  </h2>
                  <p className="text-xl text-gray-600">
                    Just a few details to get you started
                  </p>
                </div>

                <div className="max-w-md mx-auto">
                  <div className="bg-gray-50 rounded-2xl p-8 mb-8">
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                      />
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                      />
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="••••••••"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Must be at least 8 characters
                      </p>
                    </div>

                    <div className="flex items-start mb-6">
                      <input
                        type="checkbox"
                        id="terms"
                        className="mt-1 w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <label htmlFor="terms" className="ml-3 text-sm text-gray-600 cursor-pointer">
                        I agree to the Terms of Service and Privacy Policy
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={handleBack}
                      className="flex-1 px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={!formData.name || !formData.email || !formData.password}
                      className={`flex-1 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap ${
                        formData.name && formData.email && formData.password
                          ? 'bg-teal-500 text-white hover:bg-teal-600 shadow-lg shadow-teal-500/30 cursor-pointer'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Choose Template */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-12">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Choose Your Starting Template
                  </h2>
                  <p className="text-xl text-gray-600">
                    Pick a template to customize or start from scratch
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`relative rounded-2xl overflow-hidden border-2 transition-all cursor-pointer ${
                        selectedTemplate === template.id
                          ? 'border-teal-500 shadow-2xl shadow-teal-500/20 scale-105'
                          : 'border-gray-200 hover:border-teal-300 hover:shadow-xl'
                      }`}
                    >
                      <div className="h-80 bg-gray-50">
                        <img
                          src={template.image}
                          alt={template.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="p-4 bg-white">
                        <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {template.name}
                        </h3>
                        <p className="text-sm text-gray-600">{template.category}</p>
                      </div>

                      {selectedTemplate === template.id && (
                        <div className="absolute top-4 right-4">
                          <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center shadow-lg">
                            <i className="ri-check-line text-white text-xl"></i>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="text-center mb-8">
                  <button
                    onClick={() => setSelectedTemplate(0)}
                    className={`px-8 py-3 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedTemplate === 0
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-add-line mr-2"></i>
                    Start From Scratch
                  </button>
                </div>

                <div className="flex gap-4 max-w-md mx-auto">
                  <button
                    onClick={handleBack}
                    className="flex-1 px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleLaunch}
                    disabled={selectedTemplate === null}
                    className={`flex-1 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap ${
                      selectedTemplate !== null
                        ? 'bg-teal-500 text-white hover:bg-teal-600 shadow-lg shadow-teal-500/30 cursor-pointer'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Launch Builder
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Footer />
    </div>
  );
}
